import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  ClipboardCheck,
  Crown,
  Gauge,
  KeyRound,
  Image,
  Mail,
  Network,
  Settings,
  TicketCheck,
  ShieldAlert,
  ShieldCheck,
  UserCog,
  UsersRound,
} from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";

import { StepUpPanel } from "../account/SecurityPage";
import { EmptyState, ErrorState, LoadingState } from "../account/components";
import { Account, CurrentSession, MfaStatus, OperatorPermission, api } from "../api";
import { WorkspaceShell } from "../WorkspaceShell";
import {
  AccessPage,
  AccountsPage,
  AppealsPage,
  AuditPage,
  KeysPage,
  OperatorOverviewPage,
  ProductionReviewsPage,
  RiskPage,
} from "./OperatorPages";
import { SuperAdminPage } from "./SuperAdminPage";
import { ManagedSettingsPage } from "./ManagedSettingsPage";
import { RegistrationGovernancePage } from "./RegistrationGovernancePage";
import { ExternalProviderSettingsPage } from "./ExternalProviderSettingsPage";
import { MailAdministrationPage } from "./MailAdministrationPage";
import { BrandingSettingsPage } from "./BrandingSettingsPage";

const pages = [
  { id: "overview", label: "运营总览", icon: Gauge },
  { id: "accounts", label: "账号处置", icon: UserCog, any: ["ACCOUNT_SUPPORT", "ACCOUNT_ENFORCEMENT", "RISK_REVIEW", "OPERATOR_ADMIN"] },
  { id: "appeals", label: "用户申诉", icon: UsersRound, any: ["ACCOUNT_SUPPORT"] },
  { id: "reviews", label: "生产审核", icon: ClipboardCheck, any: ["PRODUCTION_REVIEW"] },
  { id: "risk", label: "风险案件", icon: ShieldAlert, any: ["RISK_REVIEW"] },
  { id: "audit", label: "平台审计", icon: Activity, any: ["AUDIT_READ"] },
  { id: "keys", label: "密钥轮换", icon: KeyRound, any: ["KEY_ROTATION"] },
  { id: "access", label: "职责治理", icon: ShieldCheck, any: ["OPERATOR_ADMIN"] },
  { id: "super-admin", label: "账号管理", icon: Crown, any: ["SUPER_ADMIN"] },
  { id: "registration", label: "注册治理", icon: TicketCheck, any: ["SUPER_ADMIN"] },
  { id: "providers", label: "登录提供商", icon: Network, any: ["SUPER_ADMIN"] },
  { id: "mail", label: "邮件投递", icon: Mail, any: ["SUPER_ADMIN"] },
  { id: "branding", label: "品牌设置", icon: Image, any: ["SUPER_ADMIN"] },
  { id: "settings", label: "系统设置", icon: Settings, any: ["SUPER_ADMIN"] },
] as const;

type OperatorPageId = (typeof pages)[number]["id"];

function pageFromPath(): OperatorPageId {
  const value = window.location.pathname.match(/^\/operator(?:\/([^/]+))?$/)?.[1] ?? "overview";
  return pages.some((page) => page.id === value) ? value as OperatorPageId : "overview";
}

export function OperatorCenter({ account, session, onLoggedOut }: { account: Account; session: CurrentSession; onLoggedOut: () => void }) {
  const [page, setPage] = useState<OperatorPageId>(pageFromPath);
  const [menuOpen, setMenuOpen] = useState(false);
  const [assuranceInvalidated, setAssuranceInvalidated] = useState(false);
  const reduceMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const operator = useQuery({ queryKey: ["operator-session"], queryFn: () => api<{ permissions: OperatorPermission[] }>("/api/v1/operator/me"), retry: false });
  const mfa = useQuery({ queryKey: ["mfa"], queryFn: () => api<MfaStatus>("/api/v1/mfa"), enabled: Boolean(operator.data?.permissions.length) });
  const logout = useMutation({ mutationFn: () => api<void>("/api/v1/session", { method: "DELETE" }), onSettled: () => { queryClient.clear(); onLoggedOut(); } });

  const available = pages.filter((item) => !("any" in item) || item.any.some((permission) => operator.data?.permissions.includes(permission as OperatorPermission)));
  const visiblePage = available.some((item) => item.id === page) ? page : "overview";

  useEffect(() => {
    const changed = () => setPage(pageFromPath());
    window.addEventListener("popstate", changed);
    return () => window.removeEventListener("popstate", changed);
  }, []);

  useEffect(() => {
    const invalidated = () => {
      setAssuranceInvalidated(true);
      void mfa.refetch();
    };
    window.addEventListener("evileye:step-up-required", invalidated);
    return () => window.removeEventListener("evileye:step-up-required", invalidated);
  }, [mfa]);

  useEffect(() => {
    if (operator.data && !available.some((item) => item.id === page)) {
      window.history.replaceState(null, "", "/operator");
      setPage("overview");
    }
  }, [available, operator.data, page]);

  function navigate(next: OperatorPageId) {
    window.history.pushState(null, "", next === "overview" ? "/operator" : `/operator/${next}`);
    setPage(next);
    setMenuOpen(false);
  }

  if (operator.isPending) return <main className="bootstrap-state"><LoadingState label="正在验证运营职责" /></main>;
  if (operator.error || !operator.data) return <main className="bootstrap-state"><ErrorState error={operator.error} retry={() => void operator.refetch()} /></main>;
  if (operator.data.permissions.length === 0) {
    return <main className="bootstrap-state"><EmptyState icon={ShieldCheck} title="没有运营职责" detail="当前账号无法进入运营控制台。" /></main>;
  }

  const permissions = operator.data.permissions;
  const ready = Boolean(!assuranceInvalidated && mfa.data?.assurance.recent && mfa.data.assurance.level >= 2);

  const verified = () => {
    setAssuranceInvalidated(false);
    void mfa.refetch();
    void queryClient.invalidateQueries({ queryKey: ["session"] });
    void queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0] ?? "").startsWith("operator-"),
    });
  };

  return (
    <WorkspaceShell
      variant="operator"
      workspaceLabel="运营控制台"
      workspaceDescription="审查、处置与平台安全治理"
      workspaceIcon={ShieldCheck}
      navLabel={`${permissions.length} 项运营职责`}
      navItems={available}
      activeId={visiblePage}
      onNavigate={navigate}
      account={account}
      statusLabel={ready ? "近期验证 AAL2" : "需要完成 AAL2 验证"}
      statusTone={ready ? "good" : "warning"}
      menuOpen={menuOpen}
      setMenuOpen={setMenuOpen}
      loggingOut={logout.isPending}
      onLogout={() => logout.mutate()}
      showOperator
    >
        {mfa.isPending && <div className="developer-step-up"><LoadingState label="正在确认安全级别" /></div>}
        {mfa.error && <div className="developer-step-up"><ErrorState error={mfa.error} retry={() => void mfa.refetch()} /></div>}
        {mfa.data && !ready && <div className="developer-step-up"><StepUpPanel minimumLevel={2} status={mfa.data} onVerified={verified} /></div>}
        {ready && <AnimatePresence mode="wait"><motion.div className="developer-stage operator-stage" key={visiblePage} initial={reduceMotion ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={reduceMotion ? undefined : { opacity: 0, y: -3 }} transition={{ duration: 0.16 }}>
          {visiblePage === "overview" && <OperatorOverviewPage permissions={permissions} session={session} />}
          {visiblePage === "accounts" && <AccountsPage permissions={permissions} />}
          {visiblePage === "appeals" && <AppealsPage />}
          {visiblePage === "reviews" && <ProductionReviewsPage />}
          {visiblePage === "risk" && <RiskPage />}
          {visiblePage === "audit" && <AuditPage />}
          {visiblePage === "keys" && <KeysPage />}
          {visiblePage === "access" && <AccessPage permissions={permissions} />}
          {visiblePage === "super-admin" && <SuperAdminPage currentSubjectId={session.subjectId} />}
          {visiblePage === "registration" && <RegistrationGovernancePage />}
          {visiblePage === "providers" && <ExternalProviderSettingsPage />}
          {visiblePage === "mail" && <MailAdministrationPage />}
          {visiblePage === "branding" && <BrandingSettingsPage />}
          {visiblePage === "settings" && <ManagedSettingsPage />}
        </motion.div></AnimatePresence>}
    </WorkspaceShell>
  );
}
